// Project-only templates. Cell indices follow the geometry engine: row 0 is
// the BOTTOM of a portrait module. Keep aligned with pv_core.panel_topology.
export const PANEL_PRESETS = [
  { id: "full_length", label: "Ολόκληρες κυψέλες — 3 κατακόρυφες ζώνες", layout: "full", columns: 6, rows: 12, sections: 3, axis: "columns" },
  { id: "full_width", label: "Ολόκληρες κυψέλες — 4 οριζόντιες ζώνες", layout: "full", columns: 8, rows: 12, sections: 4, axis: "rows" },
  { id: "half_shared", label: "Half-cut — 6 ομάδες / 3 κοινές δίοδοι", layout: "half_shared", columns: 6, rows: 24, sections: 3, axis: "columns" },
  { id: "half_narrow", label: "Half-cut στενό — 4 ομάδες / 2 κοινές δίοδοι", layout: "half_shared", columns: 4, rows: 24, sections: 2, axis: "columns" },
  { id: "half_independent", label: "Half-cut — 6 ομάδες / 6 ανεξάρτητες δίοδοι", layout: "half_independent", columns: 6, rows: 24, sections: 3, axis: "columns" },
  { id: "third_twin5", label: "Twin third-cut — 5 στήλες / 3 κοινές δίοδοι", layout: "third_twin5", columns: 5, rows: 22, sections: 3, axis: "columns" },
  { id: "full_no_bypass", label: "Ολόκληρες κυψέλες — χωρίς διόδους παράκαμψης", layout: "full_no_bypass", columns: 6, rows: 12, sections: 1, axis: "columns" },
  { id: "half_no_bypass", label: "Half-cut — δύο παράλληλα μισά χωρίς διόδους", layout: "half_no_bypass", columns: 6, rows: 24, sections: 1, axis: "columns" },
];

export const fullCellLayout = topology => ["full", "full_no_bypass"].includes(topology.layout);
export const hasBypassDiodes = topology => !["full_no_bypass", "half_no_bypass"].includes(topology.layout);

export function applyPanelPreset(topology, id, physicalCount) {
  const preset = PANEL_PRESETS.find(item => item.id === id);
  if (!preset) throw new Error("Άγνωστο πρότυπο διάταξης.");
  const candidate = Number(physicalCount) / preset.columns;
  const divisor = fullCellLayout(preset) ? (preset.axis === "rows" ? preset.sections : 1) : 2;
  const rows = Number.isInteger(candidate) && candidate >= 2 && candidate <= 60 && candidate % divisor === 0 && candidate * preset.columns <= 480
    ? candidate : preset.rows;
  const { cellGroups, ...base } = topology;
  return { ...base, version: 2, preset: id, layout: preset.layout, cellRows: rows,
    cellColumns: preset.columns, sections: preset.sections, sectionAxis: preset.axis,
    diodeVoltage: topology.diodeVoltage ?? 0.5, assumption: true };
}

export function matchingPanelPreset(topology) {
  const count = Number(topology.cellRows) * Number(topology.cellColumns);
  return PANEL_PRESETS.find(preset => preset.layout === topology.layout &&
    preset.columns === Number(topology.cellColumns) && preset.sections === Number(topology.sections) &&
    preset.axis === (topology.sectionAxis || "columns") &&
    applyPanelPreset(topology, preset.id, count).cellRows === Number(topology.cellRows)) || null;
}

// Restricted series/parallel templates, not an arbitrary circuit editor.
export function panelCellGroups(topology) {
  const rows = Number(topology.cellRows), columns = Number(topology.cellColumns), sections = Number(topology.sections);
  const halves = fullCellLayout(topology) ? 1 : 2;
  if (![rows, columns, sections].every(n => Number.isInteger(n) && n > 0) || rows * columns > 480 || rows % halves) return [];
  const third = topology.layout === "third_twin5";
  const axis = topology.sectionAxis || "columns";
  const extent = axis === "rows" ? rows / halves : columns;
  if (third ? columns !== 5 || sections !== 3 || axis !== "columns" : extent % sections !== 0) return [];
  const groups = Array.from({ length: halves * sections }, (_, index) => ({
    branch: Math.floor(index / sections), section: index % sections,
    // Zero identifies an unprotected branch; it is not a bypass diode.
    diode: !hasBypassDiodes(topology) ? 0 : topology.layout === "half_independent" ? index + 1 : index % sections + 1, cells: [],
  }));
  for (let row = 0; row < rows; row++) for (let col = 0; col < columns; col++) {
    const branch = Math.floor(row / (rows / halves));
    const section = third ? (col < 2 ? 0 : col === 2 ? 1 : 2)
      : Math.floor((axis === "rows" ? row % (rows / halves) : col) / (extent / sections));
    groups[branch * sections + section].cells.push(row * columns + col);
  }
  return groups;
}

// JSON objects can return from storage with a different property order. The
// wiring is identified by each group's branch, section, diode and cells, not
// by the byte-for-byte JSON representation or array order.
export function panelCellGroupsMatch(topology) {
  if (topology.cellGroups === undefined) return true;
  const expected = panelCellGroups(topology);
  if (!Array.isArray(topology.cellGroups) || topology.cellGroups.length !== expected.length) return false;
  const key = group => `${group.branch}:${group.section}:${group.diode}`;
  const byKey = new Map(expected.map(group => [key(group), group.cells]));
  const seen = new Set();
  for (const group of topology.cellGroups) {
    if (!group || ![group.branch, group.section, group.diode].every(Number.isInteger) ||
        !Array.isArray(group.cells) || !group.cells.every(Number.isInteger)) return false;
    const id = key(group), cells = byKey.get(id);
    if (seen.has(id) || !cells || group.cells.length !== cells.length) return false;
    seen.add(id);
    const sorted = [...group.cells].sort((a, b) => a - b);
    if (sorted.some((cell, index) => cell !== cells[index])) return false;
  }
  return true;
}

export function savedPanelTopology(draft) {
  const { diodeVoltage, ...rest } = draft;
  return { ...rest, ...(hasBypassDiodes(draft) ? { diodeVoltage } : {}), version: 2, cellGroups: panelCellGroups(draft) };
}
